import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"

import { defaultAxiosInstance } from "services/client"

import { ApiStatus, ResponseData } from "./common"

export interface CalendarObject {
  arrival: boolean
  available: boolean
  base_price: number
  departure: boolean
  max_nights: number
  min_nights: number
  valid_from: string
}

interface PropertyRequestData {
  propertyId: string
}

export interface PropertyRequest extends Partial<AxiosRequestConfig> {
  params: PropertyRequestData
}

export interface PropertyCalendarDataType {
  active: boolean
  averagePrice: number
  calendar: CalendarObject[]
  calendarRate: CalendarObject
  checkArrivalDeparture: boolean
  isHide: boolean
  checkInDate: string
  checkOutDate: string
  id: string
  noOfNights: number
}

export interface PropertyCalendarResponseData extends ResponseData {
  data: PropertyCalendarDataType
}

export interface PropertyCalendarResponse extends AxiosResponse {
  data: PropertyCalendarResponseData
}

const defaultUrlV1 = `${process.env.REACT_APP_BASE_API_URL || ""}`

export const defaultUrl = `${process.env.REACT_APP_BASE_API_URL_V2 || ""}`

export function getPropertyCalendarApi(
  {
    params,
    method = "get",
    url = `${defaultUrl}/property/calendar-data/${params.propertyId}`,
    ...config
  }: PropertyRequest,
  axiosInstance: AxiosInstance,
): Promise<PropertyCalendarResponse> {
  return axiosInstance({
    method,
    url,
    ...config,
  }) as Promise<PropertyCalendarResponse>
}

// calender apply button api call
export interface GetPrequoteRequest extends Partial<AxiosRequestConfig> {
  params: {
    adultNumber: number
    childNumber: number
    dayCount: number
    endDate: string
    pet: boolean
    petNumber?: number | null
    preQuoteFlag: boolean
    propertyId: string
    startDate: string
    isRequestFromHost: boolean
    pmcId: string
    pmsSystem: string
  }
}

export type PrequoteObjectTypeDays = {
  dailyRent: number
  date: string
}
export type PrequoteObjectTypeFees = {
  applied?: string
  currency?: string
  description: string
  id: number
  percent: number
  value: number
  displayAsRent?: boolean
}
export type PrequoteObjectTypeTaxes = {
  currency?: string
  description: string
  id: number
  percent: number
  taxId?: number
  type?: string
  value: number
}
export interface CancellationPolicyType {
  gracePeriod: number | null
  gracePeriodPercentage: number | null
  policy: {
    id: string | number | null
    category: string
    percentage: number
    validFrom: number
    validTo?: number | null
    description: string
    updatedDt: number
  }[]
}
export interface PrequoteObjectType {
  cancellationPolicy: CancellationPolicyType
  code: string
  currency: string
  days: PrequoteObjectTypeDays[]
  fees: PrequoteObjectTypeFees[]
  maxPrice: number
  message: string
  minPrice: number
  originalRent: number
  originalServiceFee: number
  pdpFees: number
  pmcid: string
  prequoteResponseId: string
  priceSimulatorId: string
  rent: number
  savedAmount: number
  serviceFee: number
  success: boolean
  taxes: PrequoteObjectTypeTaxes[]
  unitid: string // 67.2
  totalSavePercent?: number | null
  totalIncentivePercent?: number | null
  hostDiscountPercent: number
  hostServiceFeePercent: number
  hostServiceFee: number
  hostProcessingFee?: number | null
}

interface GetPrequoteResponseData extends ResponseData {
  body: PrequoteObjectType
  message: string
  success?: boolean
  status: number
}

export interface GetPrequoteResponse extends AxiosResponse {
  data: GetPrequoteResponseData
}

export function getPrequoteApi(
  { params }: GetPrequoteRequest,
  axiosInstance: AxiosInstance,
): Promise<GetPrequoteResponse> {
  const url = `${defaultUrl}/booking/host-checkout`
  return axiosInstance({
    method: "get",
    params,
    url,
  }) as Promise<GetPrequoteResponse>
}

// ----------------  list of property api call ------------------------
interface PropertyCustomTags {
  snooze: {
    startDate: number
    endDate: number
  }
  tempHold: boolean
  inDataValidation: boolean
  engineeringFixingDataIssues: boolean
  excludeGVRIncludeWS: boolean
  doNotModify: boolean
  tripOnly: boolean
  investigatingPricing: boolean
  knownHighPricing: boolean
  awaitingPmc: boolean
  awaitingPms: boolean
  dupe: boolean
}
export interface GetListOfPropertiesType {
  propertyId: string
  pmcId: string
  pmsUnitId: string
  propertyName: string
  urlPropertyName: string
  addressFull: string
  city: string
  photosCount: number
  propertyStatus: string
  minimumAgeLimit: number
  imageLink: string
  stepStatus: string
  propertyTypeDesc: string
  discountTemplateApplied: boolean
  contactTemplateApplied: boolean
  accountApplied: boolean
  isHide: boolean
  isDeleted: boolean
  createdAt: number
  weight: number
  pmId: string | null
  propertyCustomTags: PropertyCustomTags
  quarantine: boolean
  updatedDt: string
  numberOfGuest: number
  maxPetCount: number
  petAllowed: boolean
  childAllowed: boolean
  priceSimulatorStatus: string
  startDate: string | null
  endDate: string | null
  pricingConfirmDate: number | null
  pricingLastSyncDate: number | null
}

export interface GetListOfPropertiesData {
  content: GetListOfPropertiesType[]
  pageable: {
    sort: {
      empty: boolean
      sorted: boolean
      unsorted: boolean
    }
    offset: number
    pageNumber: number
    pageSize: number
    paged: boolean
    unpaged: boolean
  }
  last: boolean
  totalPages: number
  totalElements: number
  number: number
  sort: {
    empty: boolean
    sorted: boolean
    unsorted: boolean
  }
  size: number
  first: boolean
  numberOfElements: number
  empty: boolean
}

export interface GetListOfPropertiesResponseData extends ResponseData {
  data: GetListOfPropertiesData
}

export interface GetListOfPropertiesResponse extends AxiosResponse {
  data: GetListOfPropertiesResponseData
}

export interface GetListOfPropertiesRequest
  extends Partial<AxiosRequestConfig> {
  pmcId?: string
  noOfRecords?: number
  pageNo?: number
  keyword?: string
  sortDirection?: string
  sortBy?: string
  status?: string
}

const defaultUrlListOfProperties = `${
  process.env.REACT_APP_BASE_API_URL || ""
}/properties/active-properties`

export function getListOfPropertiesApi(
  { params }: GetListOfPropertiesRequest,
  axiosInstance: AxiosInstance,
): Promise<GetListOfPropertiesResponse> {
  const url = `${defaultUrlListOfProperties}`
  return axiosInstance({
    method: "get",
    params,
    url,
  }) as Promise<GetListOfPropertiesResponse>
}

// ----------------  get last confirm date api call ------------------------

export interface GetLastConfirmDateResponseData extends AxiosResponse {
  message: string
  data: {
    lastConfirmDate: string
  }
  apiStatus: ApiStatus
}

export interface GetLastConfirmDateResponse extends AxiosResponse {
  data: GetLastConfirmDateResponseData
}

export interface GetLastConfirmDateRequest {
  params: { pmcId: string; unitId: string }
}

export function getLastConfirmDateApi(
  { params }: GetLastConfirmDateRequest,
  axiosInstance: AxiosInstance,
): Promise<GetLastConfirmDateResponse> {
  const url = `${defaultUrlV1}/properties/last-confirm-price`
  return axiosInstance({
    method: "get",
    params,
    url,
  }) as Promise<GetLastConfirmDateResponse>
}

// ----------------  confirm pricing button api call -------------------------
export interface GetConfirmPricingResponse extends AxiosResponse {
  data: {
    message: string
    apiStatus: ApiStatus
  }
}

export interface GetConfirmPricingRequest {
  params: {
    priceSimulatorId: string
    confirmPriceSimulator: boolean
  }
}

export function getConfirmPricingApi(
  { params }: GetConfirmPricingRequest,
  axiosInstance: AxiosInstance,
): Promise<GetConfirmPricingResponse> {
  const url = `${defaultUrlV1}/properties/confirm-pricing`
  return axiosInstance({
    method: "get",
    params,
    url,
  }) as Promise<GetConfirmPricingResponse>
}

// ----------------  confirm all pricing button api call -------------------------

export interface GetConfirmAllPricingResponse extends AxiosResponse {
  data: {
    message: string
    apiStatus: ApiStatus
  }
}

export interface GetConfirmAllPricingRequest {
  params: {
    pmcId: string
    propertyIds: string[]
    allUpdate: boolean
  }
}

export function getConfirmAllPricingApi(
  { params }: GetConfirmAllPricingRequest,
  axiosInstance: AxiosInstance,
): Promise<GetConfirmAllPricingResponse> {
  const url = `${defaultUrlV1}/properties/confirm-pricing/all`
  return axiosInstance({
    method: "post",
    data: params,
    url,
  }) as Promise<GetConfirmAllPricingResponse>
}

// ----------- support button api call -------------
export interface SupportResponse extends AxiosResponse {
  data: {
    message: string
    apiStatus: ApiStatus
  }
}

export interface SupportRequest extends Partial<AxiosRequestConfig> {
  params: {
    propertyName: string
    email: string
    phoneNumber: string
    unitId: string
    pmcId: string
    priceDiscrepancies: Record<string, number>
    details: string
    checkInDate: string
    checkOutDate: string
    noOfGuest: number
    noOfChild: number
    maxPets: number
  }
}

export function supportRequestApi(
  {
    params,
    method = "post",
    url = `${defaultUrlV1}/properties/submit-support-request`,
    ...config
  }: SupportRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<SupportResponse> {
  return axiosInstance({
    data: params,
    method,
    url,
    ...config,
  }) as Promise<SupportResponse>
}

// fetch support details ---------------

export interface FetchSupportDetailResponseData extends AxiosResponse {
  totalCount: number
  uniquePriceDiscrepancyNames: string[]
}

export interface FetchSupportDetailResponse extends AxiosResponse {
  data: {
    message: string
    apiStatus: ApiStatus
    data: FetchSupportDetailResponseData
  }
}

export interface GetFetchSupportDetailsRequest {
  params: {
    pmcId: string
    checkInDate: string
    checkOutDate: string
    unitId: string
  }
}

export function fetchSupportDetailsApi(
  { params }: GetFetchSupportDetailsRequest,
  axiosInstance: AxiosInstance,
): Promise<FetchSupportDetailResponse> {
  const url = `${defaultUrlV1}/properties/fetch-price-support`
  return axiosInstance({
    method: "get",
    params,
    url,
  }) as Promise<FetchSupportDetailResponse>
}
